import type { TaiKhoanCuaHang, TaiKhoanCuaHangInput } from '@/types/schemas';
import { ActionBar, DeleteDialog, FormDialog } from './components';
import { ErpDataTableV2 } from '@/components/custom/erp';
import { getDataTableColumns } from './cols-definition';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';
import { FormValues } from './schemas';

const PAGE_SIZE = 20;

export default function DanhMucTaiKhoanChiTietTheoCuaHang() {
  const {
    data: storeAccounts,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    isLoading,
    searchData
  } = useCRUD<TaiKhoanCuaHang, TaiKhoanCuaHangInput>({
    endpoint: QUERY_KEYS.TAI_KHOAN_CUA_HANG,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<TaiKhoanCuaHang>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : storeAccounts || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const columns = getDataTableColumns();

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: columns
    }
  ];

  const handleAddStoreAccount = async (data: FormValues) => {
    const apiData: TaiKhoanCuaHangInput = {
      ma_cuahang: data.ma_cuahang,
      ma_kho: data.ma_kho,
      ma_bp: data.ma_bp,
      tk_cn: data.tk_cn,
      tk_cn2: data.tk_cn2,
      tk_tm: data.tk_tm,
      status: data.status
    };
    await addItem(apiData);
    await refreshData();
  };

  const handleUpdateStoreAccount = async (data: FormValues) => {
    if (selectedObj) {
      const apiData: TaiKhoanCuaHangInput = {
        ma_cuahang: data.ma_cuahang,
        ma_kho: data.ma_kho,
        ma_bp: data.ma_bp,
        tk_cn: data.tk_cn,
        tk_cn2: data.tk_cn2,
        tk_tm: data.tk_tm,
        status: data.status
      };
      await updateItem(selectedObj.uuid, apiData);
      await refreshData();
    }
  };

  const handleSubmit = async (data: FormValues) => {
    try {
      if (formMode === 'add') {
        await handleAddStoreAccount(data);
      } else if (formMode === 'edit' && selectedObj) {
        await handleUpdateStoreAccount(data);
      }
      handleCloseForm();
      clearSelection();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleDelete = async (uuid: string) => {
    try {
      await deleteItem(uuid);
      await refreshData();
    } catch (error) {
      console.error('Error deleting store account:', error);
      throw error;
    }
  };

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
      <ActionBar
        onAddIconClick={handleAddClick}
        onEditIconClick={handleEditClick}
        onDeleteIconClick={handleDeleteClick}
        onCopyIconClick={handleCopyClick}
        onWatchIconClick={handleViewClick}
        hasRowSelected={!!selectedObj}
      />

      <ErpDataTableV2
        tables={tables}
        onRowClick={handleRowClick}
        selectedRowId={selectedRowIndex || undefined}
        totalItems={resolvedTotalItems}
        currentPage={resolvedCurrentPage}
        onPageChange={onPageChange}
        pageSize={PAGE_SIZE}
        onSearchChange={handleSearchChange}
      />

      {showForm && (
        <FormDialog
          mode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onDeleteButtonClick={handleDeleteClick}
          onCopyButtonClick={handleCopyClick}
          onWatchButtonClick={handleViewClick}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          handleSubmit={handleSubmit}
        />
      )}

      {showDelete && (
        <DeleteDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          clearSelection={clearSelection}
          handleDelete={handleDelete}
        />
      )}
    </div>
  );
}
